import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppLayout from "@/components/AppLayout";
import { ArrowLeft, Send, Trash2, MessageCircle } from "lucide-react";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function AuthorTag({ role, name }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${role === "admin" ? "bg-sky text-white" : "bg-canvas text-stone"}`}>
        {role === "admin" ? "Teacher" : "Student"}
      </span>
      <span className="text-sm font-semibold text-ink">{name}</span>
    </div>
  );
}

export default function CommunityThread() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = user?.role === "admin" ? "admin" : "student";
  const basePath = variant === "admin" ? "/admin/community" : "/student/community";

  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/community/threads/${threadId}`);
      setThread(data.thread);
      setReplies(data.replies || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      navigate(basePath);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/community/threads/${threadId}/replies`, { body });
      const s = data?.post?.status;
      if (s === "approved") {
        toast.success("Reply posted");
        setBody("");
        load();
      } else if (s === "pending") {
        toast.success("Your reply is awaiting admin approval");
        setBody("");
      } else if (s === "rejected") {
        toast.error("Your reply was blocked: please keep it friendly and kid-safe");
        setBody("");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPosting(false);
    }
  };

  const deletePost = async (postId, isThread) => {
    if (!window.confirm(isThread ? "Delete this whole discussion?" : "Delete this reply?")) return;
    try {
      await api.delete(`/community/posts/${postId}`);
      toast.success("Deleted");
      if (isThread) navigate(basePath);
      else load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <AppLayout variant={variant}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-stone" data-testid="thread-loading">Loading discussion…</div>
      </AppLayout>
    );
  }

  if (!thread) return null;

  const canDeleteThread = user?.role === "admin" || thread.author_id === user?.id;

  return (
    <AppLayout variant={variant}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to={basePath} data-testid="back-to-community" className="inline-flex items-center gap-2 text-sky-hover font-bold text-sm mb-6 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to community
        </Link>

        <article data-testid="thread-detail" className="p-6 sm:p-8 bg-white rounded-3xl card-tactile mb-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <AuthorTag role={thread.author_role} name={thread.author_name} />
            <div className="flex items-center gap-2">
              <span className="text-xs text-pebble">{fmt(thread.created_at)}</span>
              {canDeleteThread && (
                <button
                  data-testid="delete-thread-button"
                  onClick={() => deletePost(thread.id, true)}
                  className="text-stone hover:text-watermelon"
                  title="Delete discussion"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <h1 data-testid="thread-title" className="font-heading text-3xl sm:text-4xl font-bold text-ink leading-tight mb-3">{thread.title}</h1>
          <p data-testid="thread-body" className="text-ink whitespace-pre-wrap leading-relaxed">{thread.body}</p>
        </article>

        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5 text-sky" />
          <h2 className="font-heading text-xl font-bold text-ink">{replies.length} {replies.length === 1 ? "reply" : "replies"}</h2>
        </div>

        {replies.length === 0 ? (
          <div data-testid="replies-empty" className="p-6 bg-white rounded-3xl card-tactile text-center text-stone mb-6">
            No replies yet. Be the first to respond!
          </div>
        ) : (
          <ul data-testid="replies-list" className="space-y-3 mb-6">
            {replies.map((r) => {
              const canDelete = user?.role === "admin" || r.author_id === user?.id;
              return (
                <li key={r.id} data-testid={`reply-${r.id}`} className="p-5 bg-white rounded-3xl card-tactile">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <AuthorTag role={r.author_role} name={r.author_name} />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-pebble">{fmt(r.created_at)}</span>
                      {canDelete && (
                        <button
                          data-testid={`delete-reply-${r.id}`}
                          onClick={() => deletePost(r.id, false)}
                          className="text-stone hover:text-watermelon"
                          title="Delete reply"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-ink whitespace-pre-wrap leading-relaxed">{r.body}</p>
                </li>
              );
            })}
          </ul>
        )}

        <form onSubmit={submitReply} className="p-5 bg-white rounded-3xl card-tactile">
          <label className="block text-sm font-bold text-ink mb-2">Add a reply</label>
          <textarea
            data-testid="reply-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            rows={4}
            placeholder="Write your reply…"
            className="w-full px-4 py-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none resize-none"
          />
          <div className="flex items-center justify-between mt-3">
            {variant === "student" ? (
              <span className="text-xs text-stone">A teacher will review before it appears.</span>
            ) : <span />}
            <button
              type="submit"
              data-testid="reply-submit-button"
              disabled={posting || !body.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
            >
              <Send className="w-4 h-4" /> {posting ? "Sending…" : "Reply"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
